import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import OneStopPlatformImage from '../assets/一站式服务平台.png';
import '../styles/digital-talent.css';
import useRevealAnimation from '../hooks/useRevealAnimation';
import useDocumentTitle from '../hooks/useDocumentTitle';

type IntroSection = {
    title: string;
    paragraphs: string[];
};

type ContentItem = {
    title: string;
    description?: string;
    bullets?: string[];
};

type ServiceSystem = {
    title: string;
    subtitle: string;
    steps: ContentItem[];
};

type SupportService = {
    title: string;
    description: string;
};

type ProcessSection = {
    title: string;
    description: string;
    bullets?: string[];
    flowLabel?: string;
    flows?: string[];
};

const renderSupportVisual = () => (
    <div className="digital-talent-support-visual" aria-hidden="true">
        <div className="digital-talent-support-visual-card digital-talent-support-visual-card-main">
            <div className="digital-talent-support-visual-badge"></div>
            <div className="digital-talent-support-visual-lines">
                <span></span>
                <span className="is-short"></span>
            </div>
        </div>
        <div className="digital-talent-support-visual-row">
            <div className="digital-talent-support-visual-card">
                <span></span>
                <span className="is-short"></span>
            </div>
            <div className="digital-talent-support-visual-card">
                <span></span>
                <span className="is-short"></span>
            </div>
        </div>
        <div className="digital-talent-support-visual-flow">
            <div className="digital-talent-support-visual-pill"></div>
            <div className="digital-talent-support-visual-pill"></div>
            <div className="digital-talent-support-visual-pill"></div>
        </div>
    </div>
);

const DigitalTalent: React.FC = () => {
    const { t, i18n } = useTranslation();
    const pageRef = useRef<HTMLDivElement>(null);
    useRevealAnimation(pageRef);
    useDocumentTitle(t('DigitalTalent.hero.title'));

    const getStructuredValue = <T,>(key: string, fallback: T): T => {
        const value = t(key, { returnObjects: true }) as T | string;
        if (typeof value !== 'string') {
            return value;
        }

        const fallbackValue = t(key, { returnObjects: true, lng: 'zh' }) as T | string;
        return typeof fallbackValue !== 'string' ? fallbackValue : fallback;
    };

    const intro = getStructuredValue<IntroSection>('DigitalTalent.intro', {
        title: '',
        paragraphs: []
    });
    const platformParagraphs = getStructuredValue<string[]>('DigitalTalent.core.platform.paragraphs', []);
    const systems = getStructuredValue<ServiceSystem[]>('DigitalTalent.core.systems.items', []);
    const supportServices = getStructuredValue<SupportService[]>('DigitalTalent.core.supportPackage.items', []);
    const processSections = getStructuredValue<ProcessSection[]>('DigitalTalent.process.sections', []);
    const pageClassName = `digital-talent-page${i18n.resolvedLanguage === 'ja' ? ' is-ja' : ''}`;
    const [openSystemIndex, setOpenSystemIndex] = useState<number | null>(null);

    return (
        <div className={pageClassName} ref={pageRef}>
            <section className="digital-talent-hero">
                <div className="digital-talent-hero-bg">
                    <div className="digital-talent-grid" />
                    <div className="digital-talent-glow" />
                </div>
                <div className="section-container digital-talent-hero-content">
                    <div className="digital-talent-hero-copy reveal">
                        <span className="digital-talent-kicker">{t('DigitalTalent.hero.kicker')}</span>
                        <h1 className="digital-talent-hero-title">{t('DigitalTalent.hero.title')}</h1>
                        <p className="digital-talent-hero-description">{intro.paragraphs[0]}</p>
                    </div>
                </div>
            </section>

            <section className="section-container digital-talent-section">
                <div className="section-header reveal">
                    <span className="section-subtitle">{t('DigitalTalent.core.label')}</span>
                    <h2 className="section-title">{t('DigitalTalent.core.title')}</h2>
                </div>

                <article className="digital-talent-block reveal delay-1">
                    <div className="digital-talent-subsection-head">
                        <div className="digital-talent-block-mark">1</div>
                        <div>
                            <h3>{t('DigitalTalent.core.platform.title')}</h3>
                            {t('DigitalTalent.core.platform.subtitle') && <p>{t('DigitalTalent.core.platform.subtitle')}</p>}
                        </div>
                    </div>
                    <div className="digital-talent-split-layout digital-talent-platform-layout">
                        <div className="digital-talent-platform-copy">
                            {platformParagraphs[0] && (
                                <p className="digital-talent-platform-lead">{platformParagraphs[0]}</p>
                            )}
                            {platformParagraphs.slice(1).length > 0 && (
                                <div className="digital-talent-platform-note">
                                    {platformParagraphs.slice(1).map((paragraph, index) => (
                                        <p key={index}>{paragraph}</p>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="digital-talent-side-visual">
                            <img
                                src={OneStopPlatformImage}
                                alt={t('DigitalTalent.core.platform.title')}
                                className="digital-talent-visual-image digital-talent-visual-image-tall"
                            />
                        </div>
                    </div>
                </article>

                <article className="digital-talent-block reveal delay-2">
                    <div className="digital-talent-subsection-head">
                        <div className="digital-talent-block-mark">4</div>
                        <div>
                            <h3>{t('DigitalTalent.core.systems.title')}</h3>
                            {t('DigitalTalent.core.systems.subtitle') && <p>{t('DigitalTalent.core.systems.subtitle')}</p>}
                        </div>
                    </div>
                    <div className="digital-talent-system-grid">
                        {systems.map((system, index) => (
                            <section
                                className={`digital-talent-system-panel${openSystemIndex === index ? ' is-open' : ''}`}
                                key={system.title}
                            >
                                <button
                                    type="button"
                                    className="digital-talent-system-trigger"
                                    aria-expanded={openSystemIndex === index}
                                    aria-controls={`digital-talent-system-panel-${index}`}
                                    onClick={() => {
                                        setOpenSystemIndex((currentIndex) => currentIndex === index ? null : index);
                                    }}
                                >
                                    <div className="digital-talent-card-head">
                                        <div>
                                            <h3>{system.title}</h3>
                                            {system.subtitle && <p className="digital-talent-system-subtitle">{system.subtitle}</p>}
                                        </div>
                                    </div>
                                    <span className="digital-talent-system-toggle" aria-hidden="true">
                                        <span />
                                        <span />
                                    </span>
                                </button>
                                {openSystemIndex === index ? (
                                    <div
                                        id={`digital-talent-system-panel-${index}`}
                                        className="digital-talent-system-collapse is-open"
                                    >
                                        <div className="digital-talent-step-list">
                                            {system.steps.map((step, stepIndex) => (
                                                <section className="digital-talent-step" key={stepIndex}>
                                                    <div className="digital-talent-step-index">{String(stepIndex + 1).padStart(2, '0')}</div>
                                                    <div className="digital-talent-step-content">
                                                        <h4>{step.title}</h4>
                                                        {step.description && <p>{step.description}</p>}
                                                        {step.bullets && (
                                                            <ul className="digital-talent-bullet-list">
                                                                {step.bullets.map((bullet, bulletIndex) => (
                                                                    <li className="digital-talent-bullet" key={bulletIndex}>
                                                                        {bullet}
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        )}
                                                    </div>
                                                </section>
                                            ))}
                                        </div>
                                    </div>
                                ) : null}
                            </section>
                        ))}
                    </div>
                </article>

                <article className="digital-talent-block reveal delay-3">
                    <div className="digital-talent-subsection-head">
                        <div className="digital-talent-block-mark">N</div>
                        <div>
                            <h3>{t('DigitalTalent.core.supportPackage.title')}</h3>
                            {t('DigitalTalent.core.supportPackage.subtitle') && <p>{t('DigitalTalent.core.supportPackage.subtitle')}</p>}
                        </div>
                    </div>
                    <div className="digital-talent-split-layout digital-talent-split-layout-compact">
                        <div className="digital-talent-support-grid">
                            {supportServices.map((service, index) => (
                                <article className="digital-talent-support-item" key={index}>
                                    <h4>{service.title}</h4>
                                    <p>{service.description}</p>
                                </article>
                            ))}
                        </div>
                        <div className="digital-talent-side-visual">
                            {renderSupportVisual()}
                        </div>
                    </div>
                </article>
            </section>

            <section className="section-container digital-talent-section">
                <div className="section-header reveal">
                    <span className="section-subtitle">{t('DigitalTalent.process.label')}</span>
                    <h2 className="section-title">{t('DigitalTalent.process.title')}</h2>
                </div>
                <div className="digital-talent-process-stack">
                    {processSections.map((section, index) => (
                        <article
                            className="digital-talent-process-card reveal"
                            key={section.title}
                            style={{ transitionDelay: `${0.1 * (index + 1)}s` }}
                        >
                            <div className="digital-talent-card-head">
                                <div>
                                    <h3>{section.title}</h3>
                                </div>
                            </div>

                            {(section.bullets && section.bullets.length > 0) || (section.flows && section.flows.length > 0) ? (
                                <div className="digital-talent-process-content">
                                    {section.bullets && section.bullets.length > 0 && (
                                        <ul className="digital-talent-bullet-list digital-talent-bullet-list-spacious">
                                            {section.bullets.map((bullet, bulletIndex) => (
                                                <li className="digital-talent-bullet" key={bulletIndex}>
                                                    {bullet}
                                                </li>
                                            ))}
                                        </ul>
                                    )}

                                    {section.flows && section.flows.length > 0 && (
                                        <div className="digital-talent-note-box">
                                            <ul className="digital-talent-bullet-list">
                                                {section.flows.map((flow, flowIndex) => (
                                                    <li className="digital-talent-bullet" key={flowIndex}>
                                                        {flow}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            ) : null}
                        </article>
                    ))}
                </div>
            </section>

            <section className="section-container digital-talent-section">
                <div className="digital-talent-career-cta reveal">
                    <span className="digital-talent-career-kicker">{t('DigitalTalent.career.label')}</span>
                    <h2>{t('DigitalTalent.career.title')}</h2>
                    <p>{t('DigitalTalent.career.description')}</p>
                    <Link to="/contact/join" className="digital-talent-career-link">
                        {t('DigitalTalent.career.cta')}
                    </Link>
                </div>
            </section>
        </div>
    );
};

export default DigitalTalent;

